import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
	CreateCompetencyBody,
	ListCompetenciesResponseJSON,
	SingleCompetencyResponseJSON,
	UpdateCompetencyBody,
} from "@performyard/service-contracts";

import {
	archiveCompetency,
	checkCompetencyName,
	createCompetency,
	getCompetencies,
	getCompetency,
	reopenCompetency,
	updateCompetency,
} from "../../services/defs/competencies";
import type { ListCompetenciesParams } from "../../services/defs/competencies";
import type { PYUseMutationOptions, PYUseQueryOptions } from "../StandardQueryArguments";

// Query keys
export const competenciesKeys = {
	all: ["Competencies"] as const,
	list: (params?: ListCompetenciesParams) => [...competenciesKeys.all, "list", params] as const,
	detail: (id: string) => [...competenciesKeys.all, "detail", id] as const,
	checkName: (name: string, excludeId?: string) => [...competenciesKeys.all, "checkName", name, excludeId] as const,
};

// List competencies
export const useCompetencies = (
	params: ListCompetenciesParams = {},
	options?: PYUseQueryOptions<ListCompetenciesResponseJSON>
) => {
	return useQuery({
		queryKey: competenciesKeys.list(params),
		queryFn: () => getCompetencies(params),
		meta: {
			errorMessage: "There was an error getting the competencies.",
		},
		...options,
	});
};

// Get single competency
export const useCompetency = (id: string, options?: PYUseQueryOptions<SingleCompetencyResponseJSON>) => {
	return useQuery({
		queryKey: competenciesKeys.detail(id),
		queryFn: () => getCompetency(id),
		enabled: !!id,
		meta: {
			errorMessage: "There was an error getting the competency.",
		},
		...options,
	});
};

// Create competency
export const useCreateCompetency = (options?: PYUseMutationOptions) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: CreateCompetencyBody) => createCompetency(body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: competenciesKeys.all });
		},
		...options,
	});
};

// Update competency
export const useUpdateCompetency = (options?: PYUseMutationOptions) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, body }: { id: string; body: UpdateCompetencyBody }) => updateCompetency(id, body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: competenciesKeys.all });
		},
		...options,
	});
};

// Archive competency
export const useArchiveCompetency = (options?: PYUseMutationOptions) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => archiveCompetency(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: competenciesKeys.all });
		},
		...options,
	});
};

// Reopen competency
export const useReopenCompetency = (options?: PYUseMutationOptions) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => reopenCompetency(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: competenciesKeys.all });
		},
		...options,
	});
};

// Check name availability
export const useCheckCompetencyName = (
	name: string,
	excludeId?: string,
	options?: PYUseQueryOptions<{ available: boolean }>
) => {
	return useQuery({
		queryKey: competenciesKeys.checkName(name, excludeId),
		queryFn: () => checkCompetencyName(name, excludeId),
		enabled: !!name,
		meta: {
			errorMessage: "There was an error checking the competency name.",
		},
		...options,
	});
};
